import { supabase } from './supabase';

// Fenêtre d'analyse. « current » = totaux/état ; 1m/3m/1y = fenêtres glissantes (cf. limites dans l'UI).
export type AdminWindow = 'current' | '1m' | '3m' | '1y';

export interface LabeledCount {
  label: string;
  count: number;
}
export interface MonthPoint {
  month: string; // 'YYYY-MM'
  count: number;
}
export interface SchoolRow {
  domain: string;
  students: number;
  active: number; // actifs RÉELS (ouvertures) sur la fenêtre
  cvsCreated: number;
  atsAvg: number | null;
  interviews: number;
}
export interface ErrorRow {
  feature: string;
  errorType: string;
  count: number;
  distinctUsers: number;
}
/** Refus de rédiger une lettre (offre trop éloignée du CV) : une mesure, pas une panne. */
export interface RefusRow {
  feature: string;
  count: number;
  distinctUsers: number;
  /** Premiers appels de lettre de la période : le dénominateur du taux. */
  tentatives: number;
}
export interface Cohort {
  week: string; // 'YYYY-MM-DD' (lundi de la semaine d'inscription)
  size: number;
  w1Retention: number; // % de la cohorte active en J+1..J+7
}

export interface FeedbackFeatureStat {
  feature: string; // 'cv_create' | 'cv_score' | 'linkedin_audit'
  total: number;
  positive: number;
  negative: number;
  satisfaction: number; // % de positifs
}
export interface FeedbackTrendPoint {
  feature: string;
  week: string; // 'YYYY-MM-DD'
  total: number;
  satisfaction: number;
}
// Volontairement SANS user_id : on mesure la qualité, pas l'identité de l'auteur.
export interface FeedbackComment {
  feature: string;
  rating: string; // 'down' (liste des négatifs)
  comment: string;
  createdAt: string; // ISO
}

export interface AdminKpi {
  window: AdminWindow;
  generatedAt: string;
  users: {
    total: number; // cumul (état)
    newInWindow: number; // flux dans la fenêtre
    active: number; // actifs RÉELS (ouvertures de l'app) sur la fenêtre
    signupSeries: MonthPoint[];
    byAge: LabeledCount[]; // état (toute la base)
    byEducation: LabeledCount[]; // déduit IA (partiel)
    byCity: LabeledCount[]; // déduit IA (partiel)
    byDomain: LabeledCount[];
  };
  retention: {
    dau: number; // actifs sur 1 jour (fixe)
    wau: number; // 7 jours
    mau: number; // 30 jours
    stickiness: number; // % DAU/MAU
    avgActiveDays: number; // fréquence de retour : jours actifs moyens / utilisateur sur la fenêtre
    cohorts: Cohort[];
  };
  usage: {
    byFeature: LabeledCount[];
    atsAvg: number | null;
    atsDistribution: LabeledCount[];
  };
  revenue: {
    coaching: { count: number; revenue: number; byFormule: LabeledCount[] };
    subscriptions: { byPlan: LabeledCount[]; mrr: number };
  };
  errors: ErrorRow[];
  // Optionnel : absent tant que la migration 0050 n'est pas appliquée.
  refusals?: RefusRow[];
  schools: SchoolRow[];
  feedback: {
    byFeature: FeedbackFeatureStat[];
    trend: FeedbackTrendPoint[];
    recentNegative: FeedbackComment[]; // 1re page (50), paginée via fetchFeedbackComments
  };
  metrics?: AnalysisMetrics | null; // optionnel : un échec d'agrégation n'empêche pas le dashboard
  aiUsage?: AiUsage | null; // idem : la section coût IA peut manquer sans casser le reste
  apiCalls?: ApiCalls | null; // idem : compteur d'appels aux API d'offres plafonnées (migration 0052)
}

export interface Fenetres {
  jour: number;
  semaine: number;
  mois: number;
}
// Nos appels à Adzuna (compteur `reserver_appel_api`) et nos plafonds (PLAFONDS_ADZUNA, côté serveur).
export interface ApiCalls {
  adzuna: Fenetres & { plafonds: Fenetres };
}

/**
 * Où mène le rythme du mois : la consommation projetée à la fin du mois (moyenne quotidienne depuis
 * le 1er, aujourd'hui compris, jours UTC comme le compteur), et le jour où le plafond sera atteint
 * s'il l'est avant la fin du mois. Pour voir venir la limite avant le 25, pas le jour où elle tombe.
 */
export function projectionMensuelle(mois: number, plafond: number, maintenant: Date): { projection: number; jourAtteinte: number | null; part: number } {
  const jour = maintenant.getUTCDate();
  const joursDuMois = new Date(Date.UTC(maintenant.getUTCFullYear(), maintenant.getUTCMonth() + 1, 0)).getUTCDate();
  const part = plafond > 0 ? Math.round((mois / plafond) * 100) : 0;
  if (mois <= 0) return { projection: 0, jourAtteinte: null, part };
  const parJour = mois / jour;
  const projection = Math.round(parJour * joursDuMois);
  const atteinte = Math.ceil(plafond / parJour);
  return { projection, jourAtteinte: projection >= plafond ? Math.min(atteinte, joursDuMois) : null, part };
}

export interface ErrorFreq {
  code: string;
  count: number;
  pct: number;
}
export interface ScoreProgression {
  users: number;
  avgDelta: number;
}
export interface AnalysisMetrics {
  topErrors: { cv: ErrorFreq[]; linkedin: ErrorFreq[] };
  progression: { cv: ScoreProgression; linkedin: ScoreProgression };
}

// Consommation Anthropic. Les montants sont des ESTIMATIONS : la mise en cache des prompts, le
// batching et les remises contractuelles ne sont pas mesurés ici.
export interface AiUsageModeRow {
  mode: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  cost: number;
}
export interface AiUsageWeek {
  week: string;
  tokens: number;
  cost: number;
}
export interface AiUsage {
  currency: string;
  pricingMissing: string[]; // modèles consommés sans tarif saisi → coût sous-estimé
  total: {
    calls: number;
    inputTokens: number;
    outputTokens: number;
    costInput: number;
    costOutput: number;
    cost: number;
  };
  byMode: AiUsageModeRow[];
  weekly: AiUsageWeek[];
  activeUsers: number;
  perActiveUser: number;
  perCall: number;
  perAnalysis: number; // hors interview_offer_extract : coût d'un livrable, pas d'un appel
}

// Libellés lisibles des modes de facturation (les clés viennent de _shared/aiUsage.ts).
export const AI_MODE_LABEL: Record<string, string> = {
  cv_create: 'CV — création',
  cv_score: 'CV — notation',
  cv_rewrite: 'CV — réécriture de section',
  cv_recommend: 'CV — reco offre',
  cv_improve_questions: 'CV — questions',
  cv_improve_generate: 'CV — optimisation',
  linkedin_captures: 'LinkedIn — captures',
  linkedin_account: 'LinkedIn — compte',
  interview_start: 'Entretien — démarrage',
  interview_reply: 'Entretien — tour',
  interview_feedback: 'Entretien — bilan',
  interview_offer_extract: 'Entretien — extraction offre',
};

// Montants IA : petits (fractions d'unité), d'où 4 décimales — formatEuros arrondirait à zéro.
export function formatCost(amount: number, currency: string): string {
  return `${amount.toFixed(4).replace('.', ',')} ${currency}`;
}

export function formatTokens(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace('.', ',')} M`;
  if (n >= 1000) return `${Math.round(n / 1000)} k`;
  return String(n);
}

export const WINDOW_LABEL: Record<AdminWindow, string> = {
  current: 'Actuel',
  '1m': '1 mois',
  '3m': '3 mois',
  '1y': '1 an',
};

// Formatage euros sans dépendre d'Intl (support partiel sous Hermes). Entrée en centimes.
export function formatEuros(cents: number): string {
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
}

export class AdminKpiError extends Error {
  forbidden: boolean;
  constructor(message: string, forbidden = false) {
    super(message);
    this.name = 'AdminKpiError';
    this.forbidden = forbidden;
  }
}

async function readErrorCode(error: unknown): Promise<string | null> {
  const context = (error as { context?: unknown }).context;
  if (context instanceof Response) {
    try {
      const body = (await context.clone().json()) as { error?: string };
      return body?.error ?? null;
    } catch {
      return null;
    }
  }
  return null;
}

function fail(code: string | null): never {
  if (code === 'forbidden') throw new AdminKpiError('Accès réservé aux administrateurs.', true);
  throw new AdminKpiError('Chargement des indicateurs impossible.');
}

// Récupère TOUS les KPI agrégés pour la fenêtre. Toute la sécurité (rôle) + l'agrégation vivent
// côté serveur (edge admin-kpi en service-role + fonction SQL). Le client ne reçoit que des agrégats.
export async function fetchAdminKpi(window: AdminWindow): Promise<AdminKpi> {
  const { data, error } = await supabase.functions.invoke<AdminKpi & { error?: string }>('admin-kpi', {
    body: { window },
  });

  if (error) fail(await readErrorCode(error));
  if ((data as { error?: string } | null)?.error) fail((data as { error?: string }).error ?? null);

  return data as AdminKpi;
}

// Page de commentaires (positifs ou négatifs) — « charger plus » et bascule de filtre. Même edge
// admin-kpi, même contrôle admin ; agrégation service-role. Renvoie moins de `limit` quand fini.
export async function fetchFeedbackComments(
  rating: 'up' | 'down',
  offset: number,
  limit = 50,
): Promise<FeedbackComment[]> {
  const { data, error } = await supabase.functions.invoke<{ comments?: FeedbackComment[]; error?: string }>(
    'admin-kpi',
    { body: { feedbackRating: rating, feedbackOffset: offset, feedbackLimit: limit } },
  );

  if (error) fail(await readErrorCode(error));
  if (data?.error) fail(data.error);

  return data?.comments ?? [];
}
